/**
 * Flow state for the three self-serve screens.
 *
 * HELD IN MEMORY ONLY. Nothing here goes to localStorage or sessionStorage,
 * deliberately and per the standing rule about the client being a presentation
 * layer. The cost is that a refresh restarts the flow; the flow is about two
 * minutes long, and storing a prospect's hiring volumes and contract figures in
 * a browser to save them thirty seconds is not a trade worth making.
 *
 * `account` records what the prospect typed on screen two so the trial can
 * greet them and the payment screen can submit them. The real submission -
 * the one that opens a real Stripe Checkout Session and redirects the browser
 * there - is made only from pages/payment via selfServeSignupFSM, after the
 * optional trial; this context only ever holds a local copy of the details
 * for the rest of this tab's session.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import { QualificationAnswers, Quote, buildQuote } from './utils/quote';
import { FlexQuote } from './utils/flex';

export interface AccountDetails {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
}

interface SelfServeState {
  /** Which flow the backend runs (config.ts). Fixed for the life of the provider. */
  cardOnFile: boolean;
  answers: QualificationAnswers | null;
  quote: Quote | null;
  /** Card-on-file (Flex) flow only: the band the prospect chose and its per-hire price. */
  flexQuote: FlexQuote | null;
  /** Card-on-file flow: called at the end of screen one instead of completeQualification. */
  completeFlexQualification: (quote: FlexQuote) => void;
  account: AccountDetails | null;
  /** Called at the end of screen one. Derives the quote in one place. */
  completeQualification: (answers: QualificationAnswers) => void;
  /** Called at the end of screen two. Stores nothing beyond this tab. */
  completeAccount: (account: AccountDetails) => void;
  reset: () => void;
}

const SelfServeContext = createContext<SelfServeState | null>(null);

export const SelfServeProvider = ({
  children,
  cardOnFile = false,
}: {
  children: ReactNode;
  cardOnFile?: boolean;
}) => {
  const [answers, setAnswers] = useState<QualificationAnswers | null>(null);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [flexQuote, setFlexQuote] = useState<FlexQuote | null>(null);
  const [account, setAccount] = useState<AccountDetails | null>(null);

  const completeQualification = useCallback((next: QualificationAnswers) => {
    setAnswers(next);
    setQuote(buildQuote(next));
  }, []);

  const completeFlexQualification = useCallback((next: FlexQuote) => {
    setFlexQuote(next);
  }, []);

  const completeAccount = useCallback((next: AccountDetails) => {
    setAccount(next);
  }, []);

  const reset = useCallback(() => {
    setAnswers(null);
    setQuote(null);
    setFlexQuote(null);
    setAccount(null);
  }, []);

  const value = useMemo(
    () => ({
      cardOnFile,
      answers,
      quote,
      flexQuote,
      completeFlexQualification,
      account,
      completeQualification,
      completeAccount,
      reset,
    }),
    [
      cardOnFile,
      answers,
      quote,
      flexQuote,
      completeFlexQualification,
      account,
      completeQualification,
      completeAccount,
      reset,
    ],
  );

  return (
    <SelfServeContext.Provider value={value}>
      {children}
    </SelfServeContext.Provider>
  );
};

/**
 * Throws outside the provider rather than handing back a null-shaped default.
 * A silent default here would let a screen render with no quote and show an
 * empty plan recap, which looks like a data problem rather than a wiring one.
 */
export const useSelfServe = (): SelfServeState => {
  const ctx = useContext(SelfServeContext);
  if (!ctx) {
    throw new Error('useSelfServe must be used inside SelfServeProvider');
  }
  return ctx;
};
